import React, { useRef } from 'react';
import {
	AlertProps,
	ArrowDownIcon,
	breakpoints,
	fromThemeColors,
	InfoBanner,
	layout,
	styleUtils,
	ThemeColor,
} from '@citrite/web-ui-component';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { debounce } from 'lodash';
import { BannerPosition } from 'AdminNotificationService/createAdminNotificationClient';
import { useFormFactor } from 'App/Header/useFormFactor';
import { ContentProps } from 'Components/AppLayout/AdminNotificationBanner';
import { paddingAndIconOffset } from 'Components/AppLayout/MastheadInformationalBanner.styled';
import { FeatureFlag } from 'Environment/FeatureFlag';
import { useFeatureCanary } from 'utils/useFeatureCanary';
import {
	bannerHeight,
	bannerHeightDetailed,
	MastheadOffset,
} from 'Workspace/ThemeProvider';

type BannerTextAlignment = 'center' | 'left' | 'right';
interface Props {
	Content: React.ComponentType<ContentProps>;
	bannerPosition: BannerPosition;
	mastheadOffset?: MastheadOffset;
	textAlign?: BannerTextAlignment;
	isAdminBanner?: boolean;
	Banner?: React.ComponentType<AlertProps>;
}

const StyledLineClamp = styled.div<{
	expandedBanner: boolean;
	innerWidth: number;
	bannerPosition: BannerPosition;
}>`
	cursor: pointer;
	max-width: ${props => props.innerWidth - paddingAndIconOffset}px;
	display: inline-block;
	overflow: ${props => (props.expandedBanner ? 'visible' : 'hidden')};
	text-overflow: ellipsis;
	white-space: ${props => (props.expandedBanner ? 'normal' : 'nowrap')};
`;

const StyledDropdownIcon = styled(ArrowDownIcon)<{
	expandedBanner: boolean;
	bannerPosition: BannerPosition;
}>`
	cursor: pointer;
	display: inline-block;
	position: absolute;
	right: 10px;
	${props =>
		props.bannerPosition !== BannerPosition.Bottom &&
		props.expandedBanner &&
		'transform: rotate(180deg)'};
	${props =>
		props.bannerPosition === BannerPosition.Bottom &&
		!props.expandedBanner &&
		'transform: rotate(180deg)'};
`;

const StyledContainerBase = styled.div<{
	bannerPosition: BannerPosition;
	textAlign: BannerTextAlignment;
	isAdminBanner?: boolean;
}>`
	${props =>
		props.bannerPosition === BannerPosition.Bottom &&
		'position: fixed; bottom: 0px; z-index: 1;'};
	text-align: ${props => props.textAlign};
`;

const StyledContainerLegacy = styled(StyledContainerBase)`
	width: 100%;
	> div:nth-of-type(1) {
		margin-bottom: 0;
	}
`;
const StyledContainerDetailed = styled(StyledContainerBase)<{
	bannerPosition: BannerPosition;
	isAdminBanner?: boolean;
}>`
	${props => props.bannerPosition === BannerPosition.Bottom && 'left: 0; right: 0;'};

	max-width: ${breakpoints.maxContentWidthPX}px;
	width: 100%;
	box-shadow: ${styleUtils.shadow1.boxShadow};
	margin: 0 auto;
	> div:first-of-type {
		margin-bottom: 0;
		border-radius: inherit;

		> div:first-of-type {
			border-radius: inherit;
		}
	}
	${props =>
		props.isAdminBanner
			? css`
					margin: ${layout.tinySpace} auto;
					border-radius: ${layout.smallSpace};
					max-width: calc(${breakpoints.maxContentWidthPX}px - ${layout.smallSpace});
					@media (max-width: ${breakpoints.maxContentWidthPX}px) {
						max-width: calc(100vw - ${layout.smallSpace});
					}
			  `
			: css`
					border-bottom: 1px solid ${fromThemeColors(ThemeColor.border)(props)};
			  `}
`;

export function DaasNotificationBanner(props: Props) {
	const { Content, bannerPosition, mastheadOffset, isAdminBanner } = props;
	const [isBannerExpanded, setBannerExpanded] = React.useState(false);
	const [displayArrowIcon, setDisplayArrowIcon] = React.useState(true);
	const [clientWidth, updateClientWidth] = React.useState(document.body.clientWidth);
	const contentRef = useRef(null);
	const { isLargeFormFactor } = useFormFactor();
	const isCenterAlignClassicViewEnabled = useFeatureCanary(
		FeatureFlag.CenterAlignClassicView
	);

	bannerPosition === BannerPosition.Bottom &&
		mastheadOffset?.setDaasBottomOffset(
			isCenterAlignClassicViewEnabled ? bannerHeightDetailed : bannerHeight
		);
	React.useEffect(() => {
		window.addEventListener('click', onClickOutsideBanner);
		if (!isAdminBanner) {
			window.addEventListener('resize', onWindowResize);
			checkTruncation();
		}
		return () => {
			window.removeEventListener('click', onClickOutsideBanner);
			if (!isAdminBanner) {
				window.removeEventListener('resize', onWindowResize);
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const checkTruncation = () => {
		if (contentRef?.current) {
			const allContent = contentRef?.current?.scrollWidth;
			const visibleContent = contentRef?.current?.clientWidth;
			setDisplayArrowIcon(allContent > visibleContent);
		}
	};
	React.useEffect(() => {
		if (contentRef?.current) {
			checkTruncation();
		}
	}, [clientWidth]);

	const onWindowResize = debounce(() => {
		setBannerExpanded(false);
		updateClientWidth(window.document.body.clientWidth);
	}, 300);

	const onClickOutsideBanner = () => {
		setBannerExpanded(false);
	};

	const onBannerClick = (e: React.MouseEvent) => {
		if (isAdminBanner || displayArrowIcon) {
			setBannerExpanded(!isBannerExpanded);
			e.stopPropagation();
		}
	};

	const BannerComponent = props.Banner || InfoBanner;

	const StyledContainer =
		isCenterAlignClassicViewEnabled && isLargeFormFactor
			? StyledContainerDetailed
			: StyledContainerLegacy;

	return (
		<StyledContainer
			data-testid="daas-admin-banner-container"
			bannerPosition={bannerPosition}
			textAlign={props.textAlign || 'left'}
			onClick={onBannerClick}
			isAdminBanner={isAdminBanner}
		>
			<BannerComponent>
				<StyledLineClamp
					data-testid="daas-admin-banner-line-clamp"
					innerWidth={window.innerWidth}
					expandedBanner={isBannerExpanded}
					bannerPosition={bannerPosition}
					ref={contentRef}
				>
					<Content isExpanded={isBannerExpanded} />
				</StyledLineClamp>
				{(isAdminBanner || displayArrowIcon) && (
					<StyledDropdownIcon
						data-testid="daas-admin-banner-dropdown-icon"
						role="button"
						tabIndex={0}
						expandedBanner={isBannerExpanded}
						bannerPosition={bannerPosition}
						onClick={onBannerClick}
					/>
				)}
			</BannerComponent>
		</StyledContainer>
	);
}
